import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { get, post } from "api";
import { Models, PostRequest } from "api/types";
import {
  fromSavedPreferences,
  toSaveRequest,
} from "modules/personalisation/utils/personalisationMapping";
import { useUser } from "modules/user/useUser/useUser";

export type Advisor = { id: string; label: string; avatarUrl: string };
export type ConcernItem = Models<"DisplayedConcern">;

export type TriState<T> =
  | { state: "unanswered" }
  | { state: "answered"; value: T }
  | { state: "skipped" };

export type PersonalisationData = {
  advisor: TriState<Advisor>;
  concerns: TriState<string[]>;
  journey: TriState<string>;
  journeyDetails: TriState<string>;
  heritage: TriState<string>;
  skinColor: TriState<string>;
  skinSun: TriState<string>;
  skinBalance: TriState<string>;
};

type PersonalisationDataContextValue = PersonalisationData & {
  setAdvisorAnswered: (advisor: Advisor) => Promise<void>;
  skipAdvisor: () => Promise<void>;

  setConcernsAnswered: (selections: string[]) => Promise<void>;
  skipConcerns: () => Promise<void>;

  setJourneyAnswered: (journeyId: string) => Promise<void>;
  skipJourney: () => Promise<void>;

  setHeritageAnswered: (heritageId: string) => Promise<void>;
  skipHeritage: () => Promise<void>;

  setSkinColorAnswered: (skinColorId: string) => Promise<void>;
  skipSkinColor: () => Promise<void>;

  setSkinSunAnswered: (skinSunId: string) => Promise<void>;
  skipSkinSun: () => Promise<void>;

  setSkinBalanceAnswered: (skinBalanceId: string) => Promise<void>;
  skipSkinBalance: () => Promise<void>;

  setJourneyDetailsAnswered: (text: string) => Promise<void>;
  skipJourneyDetails: () => Promise<void>;

  advisorIdentity: {
    name: string;
    avatarUrl: string;
  };
};

import AdonisAvatar from "../components/adonis-advisor-preferences.png";
import AriaAvatar from "../components/aria-advisor-preferences.png";

const PersonalisationDataContext =
  createContext<PersonalisationDataContextValue | null>(null);

export const usePersonalisationData = () => {
  const ctx = useContext(PersonalisationDataContext);
  if (!ctx)
    throw new Error("usePersonalisationData must be used within provider");
  return ctx;
};

export const PersonalisationDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useUser();
  const [advisor, _setAdvisor] = useState<TriState<Advisor>>({
    state: "unanswered",
  });
  const [concerns, _setConcerns] = useState<TriState<string[]>>({
    state: "unanswered",
  });
  const [journey, _setJourney] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [journeyDetails, _setJourneyDetails] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [heritage, _setHeritage] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [skinColor, _setSkinColor] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [skinSun, _setSkinSun] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [skinBalance, _setSkinBalance] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Load existing preferences
  const { data: savedPreferences } = useQuery({
    queryKey: ["/user/preferences"],
    queryFn: () => get("/user/preferences"),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Initialize state from saved preferences when data is loaded
  useEffect(() => {
    if (savedPreferences && !isInitialized) {
      const loadedData = fromSavedPreferences(savedPreferences);

      _setAdvisor(loadedData.advisor);
      _setConcerns(loadedData.concerns);
      _setJourney(loadedData.journey);
      _setJourneyDetails(loadedData.journeyDetails);
      _setHeritage(loadedData.heritage);
      _setSkinColor(loadedData.skinColor);
      _setSkinSun(loadedData.skinSun);
      _setSkinBalance(loadedData.skinBalance);

      setIsInitialized(true);
    }
  }, [savedPreferences, isInitialized]);

  const savePreferences = useMutation({
    mutationFn: (data: PostRequest<"/user/preferences">) =>
      post("/user/preferences", data),
  });

  const fetchBasicRecommendations = useMutation({
    mutationFn: () => post("/listings/basic-recommended", undefined),
  });

  const saveCurrentState = useCallback(
    async (updatedData: Partial<PersonalisationData>) => {
      try {
        const currentData = {
          advisor,
          concerns,
          journey,
          journeyDetails,
          heritage,
          skinColor,
          skinSun,
          skinBalance,
          ...updatedData,
        };
        const backendRequest = toSaveRequest(currentData);
        await savePreferences.mutateAsync(backendRequest);
      } catch (error) {
        console.error("Failed to save preferences:", error);
      }
    },
    [
      advisor,
      concerns,
      journey,
      journeyDetails,
      heritage,
      skinColor,
      skinSun,
      skinBalance,
      savePreferences,
    ]
  );

  const value = useMemo(() => {
    return {
      advisor,
      concerns,
      journey,
      heritage,
      journeyDetails,
      skinColor,
      skinSun,
      skinBalance,

      setAdvisorAnswered: async (next: Advisor) => {
        const newState = { state: "answered", value: next } as const;
        _setAdvisor(newState);
        await saveCurrentState({ advisor: newState });
      },
      skipAdvisor: async () => {
        const newState = { state: "skipped" } as const;
        _setAdvisor(newState);
        await saveCurrentState({ advisor: newState });
      },

      setConcernsAnswered: async (next: string[]) => {
        const newState = { state: "answered", value: next } as const;
        _setConcerns(newState);
        await saveCurrentState({ concerns: newState });
        await fetchBasicRecommendations.mutateAsync();
      },
      skipConcerns: async () => {
        const newState = { state: "skipped" } as const;
        _setConcerns(newState);
        await saveCurrentState({ concerns: newState });
      },

      setJourneyAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        _setJourney(newState);
        await saveCurrentState({ journey: newState });
      },
      skipJourney: async () => {
        const newState = { state: "skipped" } as const;
        _setJourney(newState);
        await saveCurrentState({ journey: newState });
      },

      setHeritageAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        _setHeritage(newState);
        await saveCurrentState({ heritage: newState });
      },
      skipHeritage: async () => {
        const newState = { state: "skipped" } as const;
        _setHeritage(newState);
        await saveCurrentState({ heritage: newState });
      },

      setSkinColorAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        _setSkinColor(newState);
        await saveCurrentState({ skinColor: newState });
      },
      skipSkinColor: async () => {
        const newState = { state: "skipped" } as const;
        _setSkinColor(newState);
        await saveCurrentState({ skinColor: newState });
      },

      setSkinSunAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        _setSkinSun(newState);
        await saveCurrentState({ skinSun: newState });
      },
      skipSkinSun: async () => {
        const newState = { state: "skipped" } as const;
        _setSkinSun(newState);
        await saveCurrentState({ skinSun: newState });
      },

      setSkinBalanceAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        _setSkinBalance(newState);
        await saveCurrentState({ skinBalance: newState });
      },
      skipSkinBalance: async () => {
        const newState = { state: "skipped" } as const;
        _setSkinBalance(newState);
        await saveCurrentState({ skinBalance: newState });
      },

      setJourneyDetailsAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        _setJourneyDetails(newState);
        await saveCurrentState({ journeyDetails: newState });
      },
      skipJourneyDetails: async () => {
        const newState = { state: "skipped" } as const;
        _setJourneyDetails(newState);
        await saveCurrentState({ journeyDetails: newState });
      },

      advisorIdentity:
        advisor.state === "answered"
          ? {
              name: advisor.value.label,
              avatarUrl:
                advisor.value.label === "Adonis" ? AdonisAvatar : AriaAvatar,
            }
          : user?.gender === "female"
          ? {
              name: "Aria",
              avatarUrl: AriaAvatar,
            }
          : {
              name: "Adonis",
              avatarUrl: AdonisAvatar,
            },
    };
  }, [
    advisor,
    concerns,
    journey,
    heritage,
    journeyDetails,
    skinColor,
    skinSun,
    skinBalance,
    user?.gender,
    saveCurrentState,
    fetchBasicRecommendations,
  ]);

  return (
    <PersonalisationDataContext.Provider value={value}>
      {children}
    </PersonalisationDataContext.Provider>
  );
};
